import { createTheme } from '@mui/material/styles'

export const tokens = {
  bg: '#0B0E12',
  bgSoft: '#0F1318',
  surface: '#131820',
  raised: '#19202A',
  hover: '#1F2732',
  border: '#242D39',
  borderStrong: '#313C4A',
  text: '#E9EEF5',
  textMuted: '#94A3B8',
  textFaint: '#64748B',
  accent: '#2FC49B',
  accentHover: '#39D8AB',
  accentSoft: 'rgba(47, 196, 155, 0.14)',
  danger: '#F0616B',
  dangerSoft: 'rgba(240, 97, 107, 0.12)',
  warning: '#E3B341',
  shadow: 'rgba(3, 8, 14, 0.55)'
}

const bodyFont = "'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', sans-serif"
const displayFont = "'Outfit', 'Plus Jakarta Sans', system-ui, sans-serif"

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: tokens.accent,
      light: tokens.accentHover,
      contrastText: '#04231B'
    },
    secondary: {
      main: '#8AA0B8'
    },
    background: {
      default: tokens.bg,
      paper: tokens.surface
    },
    text: {
      primary: tokens.text,
      secondary: tokens.textMuted,
      disabled: tokens.textFaint
    },
    divider: tokens.border,
    error: {
      main: tokens.danger
    },
    warning: {
      main: tokens.warning
    },
    success: {
      main: tokens.accent
    },
    info: {
      main: '#6EA8FE'
    },
    action: {
      hover: tokens.hover,
      selected: tokens.accentSoft,
      active: tokens.accentSoft,
      focus: tokens.accentSoft,
      disabled: tokens.textFaint,
      disabledBackground: tokens.raised
    }
  },
  shape: {
    borderRadius: 12
  },
  typography: {
    fontFamily: bodyFont,
    h1: {
      fontFamily: displayFont,
      fontWeight: 600,
      fontSize: '2.75rem',
      lineHeight: 1.08,
      letterSpacing: '-0.03em'
    },
    h2: {
      fontFamily: displayFont,
      fontWeight: 600,
      fontSize: '2rem',
      lineHeight: 1.15,
      letterSpacing: '-0.025em'
    },
    h3: {
      fontFamily: displayFont,
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.2,
      letterSpacing: '-0.02em'
    },
    h4: {
      fontFamily: displayFont,
      fontWeight: 600,
      fontSize: '1.2rem',
      lineHeight: 1.25,
      letterSpacing: '-0.015em'
    },
    h5: {
      fontFamily: displayFont,
      fontWeight: 600,
      fontSize: '1.05rem',
      letterSpacing: '-0.01em'
    },
    h6: {
      fontFamily: bodyFont,
      fontWeight: 700,
      fontSize: '0.9375rem',
      letterSpacing: '-0.005em'
    },
    subtitle1: {
      fontWeight: 600,
      fontSize: '0.9375rem'
    },
    subtitle2: {
      fontWeight: 600,
      fontSize: '0.8125rem'
    },
    body1: {
      fontSize: '0.9375rem',
      lineHeight: 1.6
    },
    body2: {
      fontSize: '0.8125rem',
      lineHeight: 1.55
    },
    button: {
      fontWeight: 600,
      fontSize: '0.875rem',
      textTransform: 'none',
      letterSpacing: 0
    },
    caption: {
      fontSize: '0.75rem',
      letterSpacing: '0.01em'
    },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 700,
      letterSpacing: '0.14em',
      lineHeight: 1.6,
      textTransform: 'uppercase'
    }
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tokens.bg,
          backgroundImage:
            'radial-gradient(900px 500px at 12% -10%, rgba(47, 196, 155, 0.07), transparent 60%), radial-gradient(700px 500px at 100% 0%, rgba(110, 168, 254, 0.05), transparent 55%)',
          backgroundAttachment: 'fixed',
          colorScheme: 'dark'
        },
        '::selection': {
          backgroundColor: tokens.accentSoft,
          color: tokens.text
        },
        ':focus-visible': {
          outline: `2px solid ${tokens.accent}`,
          outlineOffset: 2
        }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 10,
          paddingInline: 16,
          paddingBlock: 8,
          transition: 'background-color 180ms ease, color 180ms ease, transform 120ms ease'
        },
        containedPrimary: {
          '&:hover': { backgroundColor: tokens.accentHover },
          '&:active': { transform: 'translateY(1px)' }
        },
        outlined: {
          borderColor: tokens.borderStrong,
          '&:hover': {
            borderColor: tokens.accent,
            backgroundColor: tokens.accentSoft
          }
        },
        text: {
          '&:hover': { backgroundColor: tokens.hover }
        },
        sizeSmall: {
          paddingInline: 12,
          paddingBlock: 5
        }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          color: tokens.textMuted,
          transition: 'background-color 160ms ease, color 160ms ease',
          '&:hover': { backgroundColor: tokens.hover, color: tokens.text }
        },
        sizeSmall: { padding: 6 }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.raised,
          backgroundImage: 'none',
          border: `1px solid ${tokens.border}`,
          borderRadius: 12
        }
      }
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: tokens.bgSoft,
            borderRadius: 10,
            transition: 'background-color 160ms ease'
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.border
          },
          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.borderStrong
          },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.accent,
            borderWidth: 1
          }
        }
      }
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          marginInline: 8,
          paddingBlock: 7,
          transition: 'background-color 160ms ease, color 160ms ease',
          '&.Mui-selected': {
            backgroundColor: tokens.accentSoft,
            color: tokens.text,
            '&:hover': { backgroundColor: tokens.accentSoft }
          }
        }
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: `1px solid ${tokens.border}`,
          backgroundColor: tokens.surface
        }
      }
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.bgSoft,
          backgroundImage: 'none'
        }
      }
    },
    MuiTooltip: {
      defaultProps: { arrow: true },
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.raised,
          border: `1px solid ${tokens.border}`,
          color: tokens.text,
          fontSize: '0.75rem',
          fontWeight: 500,
          padding: '6px 10px'
        },
        arrow: { color: tokens.raised }
      }
    },
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: { backgroundColor: 'rgba(148, 163, 184, 0.12)', borderRadius: 8 }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 8, fontWeight: 600, fontSize: '0.75rem' }
      }
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10 }
      }
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${tokens.border}`,
          borderRadius: 12,
          marginTop: 6
        }
      }
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: tokens.border }
      }
    }
  }
})

export default theme
